import * as React from "react";
import { HelpCircle } from "lucide-react";
import { Button, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { AspectRatio, ContextType } from "@/services/bedrock/dtos/DesignPackage";
import type { DesignBasis } from "../NewDesignPage";
import { ASPECT_RATIO_LABELS, CONTEXT_TYPE_LABELS, FORMAT_LABELS } from "../packageStatus";

interface BriefPanelProps {
  basis: DesignBasis;
  contextType: ContextType;
  format: "single" | "carousel";
  aspectRatio: AspectRatio | "auto";
  brief: string;
  onOpenHelp(): void;
}

const SCHEMATIC_ASPECT: Record<AspectRatio | "auto", string> = {
  auto: "aspect-square",
  square_1_1: "aspect-square",
  portrait_4_5: "aspect-[4/5]",
  portrait_9_16: "aspect-[9/16]",
};

export function briefMetaLine(
  contextType: ContextType,
  format: "single" | "carousel",
  aspectRatio: AspectRatio | "auto"
): string {
  const size = aspectRatio === "auto" ? "Auto" : ASPECT_RATIO_LABELS[aspectRatio];
  return `${CONTEXT_TYPE_LABELS[contextType]} · ${FORMAT_LABELS[format]} · ${size}`;
}

const BriefSchematic: React.FC<{ format: "single" | "carousel"; aspectRatio: AspectRatio | "auto" }> = ({
  format,
  aspectRatio,
}) => {
  const box = cn(
    "w-full rounded-lg border-2 bg-white",
    SCHEMATIC_ASPECT[aspectRatio],
    aspectRatio === "auto" ? "border-dotted border-slate-300" : "border-dashed border-slate-300"
  );

  return (
    <div className="flex flex-col items-center gap-1.5" data-testid="brief-schematic" data-aspect={aspectRatio}>
      <div className="relative w-28">
        {format === "carousel" && (
          <>
            <div className={cn(box, "absolute inset-0 translate-x-3 -translate-y-3 opacity-50")} aria-hidden="true" />
            <div
              className={cn(box, "absolute inset-0 translate-x-1.5 -translate-y-1.5 opacity-75")}
              aria-hidden="true"
            />
          </>
        )}
        <div className={cn(box, "relative flex items-center justify-center")}>
          {aspectRatio === "auto" && <span className="text-xs font-semibold text-slate-400">Auto</span>}
        </div>
      </div>
      {format === "carousel" && <span className="text-[11px] text-slate-400">2 to 5 slides</span>}
    </div>
  );
};

/** Splits the brief into sentences for display while keeping its text content identical to `brief`. */
function briefLines(brief: string): string[] {
  return brief.split(/(?<=[.!?])\s+/).filter(Boolean);
}

export const BriefPanel: React.FC<BriefPanelProps> = ({ contextType, format, aspectRatio, brief, onOpenHelp }) => {
  const lines = briefLines(brief);

  return (
    <Card className="rounded-2xl border-slate-200 shadow-sm">
      <Card.Header>
        <Card.Title>Your brief</Card.Title>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        <BriefSchematic format={format} aspectRatio={aspectRatio} />
        <span className="text-xs font-semibold text-slate-500 text-center">
          {briefMetaLine(contextType, format, aspectRatio)}
        </span>
        <p className="text-sm text-slate-700 leading-relaxed" aria-live="polite" data-testid="brief-text">
          {lines.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && " "}
              <span className="block">{line}</span>
            </React.Fragment>
          ))}
        </p>
        <Button variant="link" size="inline" className="self-start" onClick={onOpenHelp}>
          <HelpCircle aria-hidden="true" />
          What can I ask for?
        </Button>
      </Card.Content>
    </Card>
  );
};
